import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import SecondaryButton from "../SecondaryButton/SecondaryButton";
import PlainTextButton from "../PlainTextButton/PlainTextButton";
import Title from "../Title/Title";
import BookingLinkInput from "../BookingLinkInput/BookingLinkInput";
import CopyText from "../CopyText/CopyText";
import { InputV2 } from "../InputV2/InputV2";
import InputNumber from "../InputNumber/InputNumber";
import CommissionTemplateFeeFields, { CommissionFeeValue } from "../CommissionContractFields/CommissionTemplateFeeFields";
import FormConditionsEditor from "../FormConditionsEditor/FormConditionsEditor";
import OnboardingWizardLayout, { OnboardingLanguageSwitcher } from "../OnboardingFlow/OnboardingWizardLayout";
import CommissionContractAliasAPI from "../../api/CommissionContractAliasAPI";
import ComissionContractsAPI from "../../api/ComissionContractsAPI";
import CommissionContractOffersAPI, { CommissionContractOfferDto } from "../../api/CommissionContractOffersAPI";
import { toastService } from "../../services/ToastService";
import { ONBOARDING_PLAN_PATH } from "../../utils/onboardingFirstWin";
import { createEmptyOffer } from "../../hooks/commissionOfferEditor";
import { Settings } from "../../store/reducers/AccountSlice";

const OnboardingDropFlow: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/";
  const settings = useSelector((state: any) => state.settings.value) as Settings;
  const brandName = settings?.tenantName || "";
  const defaultAlias = brandName.toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/^-+|-+$/g, "");
  const emptyOffer = createEmptyOffer(settings);

  const [stepIndex, setStepIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [loading, setLoading] = useState(false);
  const [alias, setAlias] = useState(defaultAlias);
  const [existingAlias, setExistingAlias] = useState<string | undefined>();
  const [isLinkValid, setIsLinkValid] = useState(false);
  const [dropLink, setDropLink] = useState("");
  const [customOfferName, setCustomOfferName] = useState<string | null>(null);
  const [saleDuration, setSaleDuration] = useState(180);
  const [dropFee, setDropFee] = useState<CommissionFeeValue>({
    price: emptyOffer.dropFeePrice ?? 0,
    currency: emptyOffer.dropFeeCurrency ?? "",
  });
  const [renewalFee, setRenewalFee] = useState<CommissionFeeValue>({
    price: emptyOffer.renewalFeePrice ?? 0,
    currency: emptyOffer.renewalFeeCurrency ?? "",
  });
  const [listingPaid, setListingPaid] = useState(false);
  const [renewalPaid, setRenewalPaid] = useState(false);
  const [customConditions, setCustomConditions] = useState<string[] | null>(null);

  const offerName = customOfferName ?? ((t("commission_payment_offers.default_name") as string) || "Standard");
  const conditions = customConditions ?? [
    t("onboarding.drop.step3.default_1") as string,
    t("onboarding.drop.step3.default_2") as string,
    t("onboarding.drop.step3.default_3") as string,
  ];

  const isWow = stepIndex === 5;
  const namedConditions = conditions.map((condition) => condition.trim()).filter(Boolean);

  const canProceed = () => {
    if (stepIndex === 1) {
      return offerName.trim().length > 0 && Number.isInteger(saleDuration) && saleDuration >= 1;
    }
    if (stepIndex === 2) {
      const listingOk = !listingPaid || (Number.isFinite(dropFee.price) && dropFee.price > 0);
      const renewalOk = !renewalPaid || (Number.isFinite(renewalFee.price) && renewalFee.price > 0);
      return listingOk && renewalOk;
    }
    if (stepIndex === 3) return namedConditions.length >= 1;
    if (stepIndex === 4) return isLinkValid && !!alias.trim();
    return true;
  };

  useEffect(() => {
    CommissionContractAliasAPI.getAlias()
      .then((res) => {
        if (res.data) {
          setAlias(res.data);
          setExistingAlias(res.data);
        }
      })
      .catch((error) => {
        console.error("Failed to load commission alias", { error });
      });
  }, []);

  const goToPlan = () => {
    navigate(ONBOARDING_PLAN_PATH, { state: { from: redirectTo, firstWin: "drop" } });
  };

  const advance = () => {
    setFade(false);
    setTimeout(() => {
      setStepIndex((prev) => prev + 1);
      setFade(true);
    }, 300);
  };

  const goBack = () => {
    if (stepIndex === 0) return;
    setFade(false);
    setTimeout(() => {
      setStepIndex((prev) => prev - 1);
      setFade(true);
    }, 300);
  };

  const upsertOffer = async () => {
    const offerPatch: Pick<
      CommissionContractOfferDto,
      "name" | "saleDuration" | "dropFeePrice" | "dropFeeCurrency" | "renewalFeePrice" | "renewalFeeCurrency"
    > = {
      name: offerName.trim(),
      saleDuration,
      dropFeePrice: dropFee.price,
      dropFeeCurrency: dropFee.currency,
      renewalFeePrice: renewalFee.price,
      renewalFeeCurrency: renewalFee.currency,
    };
    const listed = (await CommissionContractOffersAPI.list()).data || [];
    const existing = listed.find((offer) => offer.enabled !== false) || listed[0];
    if (existing?.id) {
      await CommissionContractOffersAPI.update(existing.id, { ...existing, ...offerPatch });
      return;
    }
    await CommissionContractOffersAPI.create({
      ...createEmptyOffer(settings),
      ...offerPatch,
      enabled: true,
    });
  };

  const handleComplete = async () => {
    if (!canProceed()) {
      return;
    }
    setLoading(true);
    try {
      const template = (await ComissionContractsAPI.getTemplate("EN_US")).data;
      await ComissionContractsAPI.saveTemplate("EN_US", {
        ...template,
        formConditions: namedConditions,
      });
      await ComissionContractsAPI.updateEnabledStatus("EN_US", true);
      await CommissionContractAliasAPI.setAlias(alias);
      await upsertOffer();
      setDropLink(`${window.location.origin}/drops/${alias}`);
      advance();
    } catch (error) {
      console.error("Failed to set up drop form", { error });
      toastService.showToast(t("onboarding.drop.error") as string, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (!canProceed()) return;
    if (stepIndex === 4) {
      handleComplete();
      return;
    }
    advance();
  };

  const wizardNav = (nextText: string, nextAction: string, nextDisabled: boolean) => (
    <div className="flex flex-col items-center gap-4 w-full max-w-md">
      <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
        <SecondaryButton text={t("onboarding.drop.back") as string} fn={goBack} actionName="onboarding_drop_back" />
        <PrimaryButton tone="brand"
          text={nextText}
          fn={handleNext}
          actionName={nextAction}
          isDisabled={nextDisabled}
        />
      </div>
      <PlainTextButton tone="brand" text={t("onboarding.drop.skip") as string} onClick={goToPlan} actionName="onboarding_drop_skip" />
    </div>
  );

  const renderStep = () => {
    switch (stepIndex) {
      case 0:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl text-center">
              {t("onboarding.drop.intro.description")}
            </p>
            <PrimaryButton tone="brand"
              text={t("onboarding.drop.intro.button") as string}
              fn={handleNext}
              actionName="onboarding_drop_intro"
            />
            <PlainTextButton tone="brand"
              text={t("onboarding.drop.skip") as string}
              onClick={goToPlan}
              actionName="onboarding_drop_skip"
            />
            <OnboardingLanguageSwitcher />
          </div>
        );
      case 1:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl text-center">
              {t("onboarding.drop.offer.question")}
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl text-center">
              {t("onboarding.drop.offer.helper")}
            </p>
            <div className="w-full max-w-md space-y-4 text-left">
              <InputV2
                value={offerName}
                onChange={(e) => setCustomOfferName(e.target.value)}
                placeholder={t("onboarding.drop.offer.name_placeholder") as string}
              />
              <InputNumber
                label="commission_contract_template.sale_duration_label"
                helperText="commission_contract_template.sale_duration_hint"
                defaultValue={saleDuration}
                onChange={(e) => setSaleDuration(Number(e.target.value))}
                validationFn={(n) => Number.isFinite(n) && n >= 1 && Number.isInteger(n)}
                errorMessage="commission_payment_offers.sale_duration_invalid"
              />
            </div>
            {wizardNav(t("onboarding.drop.continue") as string, "onboarding_drop_offer_next", !canProceed())}
            <OnboardingLanguageSwitcher />
          </div>
        );
      case 2:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl text-center">
              {t("onboarding.drop.step2.question")}
            </p>
            <div className="w-full max-w-2xl rounded-xl border border-border bg-card p-4 text-left">
              <CommissionTemplateFeeFields
                dropFee={dropFee}
                renewalFee={renewalFee}
                onDropFeeChange={setDropFee}
                onRenewalFeeChange={setRenewalFee}
                onDropFeePaidChange={setListingPaid}
                onRenewalFeePaidChange={setRenewalPaid}
              />
            </div>
            {wizardNav(t("onboarding.drop.continue") as string, "onboarding_drop_fees_next", !canProceed())}
            <OnboardingLanguageSwitcher />
          </div>
        );
      case 3:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl text-center">
              {t("onboarding.drop.step3.question")}
            </p>
            <div className="w-full max-w-2xl text-left">
              <FormConditionsEditor conditions={conditions} onChange={setCustomConditions} />
            </div>
            {wizardNav(t("onboarding.drop.continue") as string, "onboarding_drop_conditions_next", !canProceed())}
            <OnboardingLanguageSwitcher />
          </div>
        );
      case 4:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl text-center">
              {t("onboarding.drop.step1.question")}
            </p>
            <div className="w-full max-w-2xl">
              <BookingLinkInput
                value={alias}
                onChange={setAlias}
                placeholder={defaultAlias}
                currentAlias={existingAlias}
                baseUrl={`${window.location.origin}/drops/`}
                checkAvailability={(value, signal) =>
                  CommissionContractAliasAPI.checkAliasAvailability(value, signal)
                }
                helperText={t("onboarding.drop.step1.helper") as string}
                onValidationChange={setIsLinkValid}
              />
            </div>
            {wizardNav(t("onboarding.drop.step1.button") as string, "onboarding_drop_complete", !canProceed())}
            <OnboardingLanguageSwitcher />
          </div>
        );
      case 5:
        return (
          <div className="flex flex-col items-center space-y-8">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <Title
                text={t("onboarding.drop.final.title") as string}
                clazz="text-3xl font-bold !text-foreground mb-4 !text-center"
              />
              <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
                {t("onboarding.drop.final.description")}
              </p>
            </div>
            <div className="w-full max-w-2xl bg-muted rounded-lg p-6 space-y-4">
              <div className="flex flex-wrap items-center gap-2 bg-card p-3 rounded-md border border-border">
                <span className="text-muted-foreground truncate flex-1 min-w-0">{dropLink}</span>
                <CopyText text={dropLink} label={t("onboarding.drop.final.copy_link") as string} />
              </div>
              <div className="space-y-3 pt-4 border-t border-border">
                <p className="font-medium text-foreground">{t("onboarding.drop.final.next_steps.title")}</p>
                <p className="text-sm text-muted-foreground">{t("onboarding.drop.final.next_steps.instagram")}</p>
                <p className="text-sm text-muted-foreground">{t("onboarding.drop.final.next_steps.website")}</p>
              </div>
            </div>
            <PrimaryButton tone="brand"
              text={t("onboarding.wow.continue") as string}
              fn={goToPlan}
              actionName="onboarding_drop_finish"
            />
            <OnboardingLanguageSwitcher />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <OnboardingWizardLayout
      fade={fade}
      loading={loading}
      loadingText={t("onboarding.drop.loading") as string}
      title={!isWow && stepIndex > 0 ? (t("onboarding.drop.title") as string) : undefined}
      showProgress={!isWow && stepIndex > 0}
      progressTotal={4}
      progressIndex={stepIndex - 1}
    >
      {renderStep()}
    </OnboardingWizardLayout>
  );
};

export default OnboardingDropFlow;
